import { FC, useCallback, useMemo } from "react";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { faChevronRight, faFileShield, faGlobe, faRefresh, faServer, faTrash } from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { useCertificate, useDeleteCertificate, useDomains, useOrderCertificate, useServers } from "@hostingbv/lib/src/api/partner/PartnerHooks";
import { Card } from "@hostingbv/lib/src/components/Card";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { DeploymentHistoryBlock } from "../components/deployments/DeploymentHistoryBlock";
import { useApiClient } from "@hostingbv/lib/src/api";
import { config } from "../config";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { useRefresh } from "@hostingbv/lib/src/components/RefreshController";

export const CertificateDetails: FC = () => {
  const { certificate: uuid } = useParams()
  const { data: certificate, isLoading } = useCertificate(uuid!)

  const { partnerClient } = useApiClient(config)
  const { can } = usePermissions(partnerClient)
  const { data: domains } = useDomains()
  const { data: servers } = useServers()
  const domain = useMemo(() => (domains ?? []).find(domain => certificate?.domains.find(cd => cd.endsWith(domain.displayName))), [domains, certificate])
  const server = useMemo(() => (servers ?? []).find(server => server.id === certificate?.windhostingServer), [servers, certificate])

  const navigate = useNavigate()
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const refresh = useRefresh()
  const { mutate: orderCertificate } = useOrderCertificate()

  const order = useCallback(async () => {
    if (!certificate) return;

    const certificateOrder = await orderCertificate({
      type: 'acme_certificate',
      customer: certificate.customer,
      server: certificate.windhostingServer,
      displayName: certificate.displayName,
      domains: certificate.domains,
      challengeType: certificate.challengeType,
      keyType: certificate.keyType,
      keyLength: certificate.keyLength
    })

    navigate('/certificates/' + certificate.id + "/finalize", { state: { certificate: certificateOrder } })
  }, [certificate, orderCertificate, navigate])

  const { mutate: deleteCertificate } = useDeleteCertificate(certificate?.id ?? '')
  const remove = useCallback(async () => {
    const deployment = await deleteCertificate()
    await awaitNextDeploymentFinished(deployment.id)
    refresh()
    navigate('/certificates')
  }, [deleteCertificate, awaitNextDeploymentFinished, refresh, navigate])

  return <ContentContainer>
    <PageIconHeader icon={faFileShield}>
      <Breadcrumbs currentPage={certificate?.displayName ?? "Certificate"} crumbs={[
        { label: "certificates", href: "/certificates" },
      ]} />
      <PageHeader>
        {certificate === null ? <span className={"block h-8 w-36 rounded-lg bg-slate-200 animate-pulse"}></span> : certificate.displayName}
      </PageHeader>
    </PageIconHeader>

    <Loading loading={isLoading}>
      {certificate && <>
        <Card title={'Details'}>
          <div className={"flex"}>
            <dl className={"flex-1"}>
              <dt className={"text-slate-500 text-xs"}>Challenge Type</dt>
              <dd className={"text-slate-900 mb-4"}>{certificate.challengeType}</dd>

              <dt className={"text-slate-500 text-xs"}>Domains</dt>
              <dd className={"text-slate-900 mb-4"}>{certificate.domains.join(', ')}</dd>

              <dt className={"text-slate-500 text-xs"}>Type</dt>
              <dd className={"text-slate-900 mb-4"}>{certificate.keyType} ({certificate.keyLength} bits)</dd>
            </dl>
            <div className="flex flex-col gap-2">
              {can('edit', 'certificates', certificate.permissions) && <Button type={'primary'} size={"sm"} text={'Reissue'} icon={faRefresh} onClick={() => order()} />}
              {can('delete', 'certificates', certificate.permissions) && <Button type={'danger'} size={"sm"} text={'Delete'} icon={faTrash} onClick={() => remove()} />}
            </div>
          </div>


          {(server || domain) && <hr className={"-mx-6 border-slate-200"} />}
          <div className={"flex flex-col xl:space-x-4 xl:flex-row"}>
            {server && <NavLink className={"mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"} to={`/servers/${certificate.windhostingServer}`}>
              <div className={"w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"}>
                <FontAwesomeIcon icon={faServer} className={"text-brand-500"} />
              </div>
              <dl className={"flex flex-col items-start flex-1"}>
                <dt className={"text-slate-500 text-xs"}>Server</dt>
                <dd className={"text-slate-900"}>{server?.displayName}</dd>
              </dl>
              <div className={"text-brand-600 flex items-center justify-end space-x-3"}>
                <div className={"text-xs uppercase tracking-wide font-medium"}>Details</div> <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </NavLink>}
            {domain && <NavLink className={"mt-4 py-3 pr-4 border border-slate-100 rounded-sm w-full flex items-center justify-between hover:bg-slate-50"} to={`/domains/${domain?.id}`}>
              <div className={"w-8 h-8 mx-4 rounded-full bg-brand-100 flex items-center justify-center"}>
                <FontAwesomeIcon icon={faGlobe} className={"text-brand-500"} />
              </div>
              <dl className={"flex flex-col items-start flex-1"}>
                <dt className={"text-slate-500 text-xs"}>Domain & DNS</dt>
                <dd className={"text-slate-900"}>{domain.displayName}</dd>
              </dl>
              <div className={"text-brand-600 flex items-center justify-end space-x-3"}>
                <div className={"text-xs uppercase tracking-wide font-medium"}>Details</div> <FontAwesomeIcon icon={faChevronRight} />
              </div>
            </NavLink>}
          </div>
        </Card>
        <DeploymentHistoryBlock entityUUID={certificate.id} />
      </>}
    </Loading>
  </ContentContainer>
}
